import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  NgZone,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { ToastService } from '../../../components/toast/toast.service';
import { AuthenticationService } from '../../services/authentication.service';
import { LoaderService } from 'src/app/core/components/loader/loader.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserV2 } from '../../interfaces/user';
import { PortalTypeEnum } from 'src/app/shared/common/PortalTypes';
import { ProviderType } from '../../interfaces/providerType';
import { environment } from 'src/environments/environment';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { GoogleSigninButtonModule } from '@abacritt/angularx-social-login';
import { PermissionsService } from '../../services/permession.service';
import { externalStoreApiKeyService } from '@gozetta/dispatch-portal';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    GoogleSigninButtonModule,
  ],

  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  @ViewChild('customGoogleButton') customGoogleButton: ElementRef | undefined;
  public loginForm!: FormGroup;
  public passwordVisible: boolean = false; // Initial state for password visibility
  public emailTouched = false;
  loadingState$: Observable<boolean> = this.loaderService.localLoaderState;
  public currentUser: Observable<UserV2>;
  private readonly currentUserSubject: BehaviorSubject<UserV2>;
  private readonly menuList: any;

  constructor(
    public fb: FormBuilder,
    public authenticationService: AuthenticationService,
    public router: Router,
    public toastService: ToastService,
    private readonly ngZone: NgZone,
    private readonly _msalService: MsalService,
    private readonly _per: PermissionsService,

    private readonly loaderService: LoaderService,
  ) {
    const userJson = localStorage.getItem('currentUser');
    let parsedUser: UserV2;

    try {
      parsedUser = userJson ? JSON.parse(userJson) : ({} as UserV2);
    } catch {
      parsedUser = {} as UserV2;
    }

    this.currentUserSubject = new BehaviorSubject<UserV2>(parsedUser);
    this.currentUser = this.currentUserSubject.asObservable();
    this.initForm();
    // Ensure MSAL is initialized properly
    this.msalServiceInitialize();
  }

  private readonly destroyRef = inject(DestroyRef);

  msalServiceInitialize() {
    this._msalService.instance
      .initialize()
      .then(() => {
        console.log('MSAL Initialized');
      })
      .catch(error => {
        console.error('MSAL Initialization Error:', error);
      });
  }
  ngAfterViewInit() {
    const gAccounts = google?.accounts;
    gAccounts.id.initialize({
      allowed_parent_origin: environment.localDomain,
      client_id: environment.client_id,
      ux_mode: 'popup', // Redirect-based OAuth avoids postMessage issues
      callback: ({ credential }: any) => {
        this.ngZone.run(() => {
          this.handleCredentialResponse(credential);
        });
      },
    });

    const createFakeGoogleWrapper = () => {
      const googleLoginWrapper = document.createElement('div');
      googleLoginWrapper.style.display = 'none'; // Hides the wrapper
      googleLoginWrapper.classList.add('custom-google-button');
      document.body.appendChild(googleLoginWrapper);

      // Render the button using the GSI API
      gAccounts.id.renderButton(googleLoginWrapper, {
        type: 'icon', // Adjust rendering type
        width: 200, // Adjust button width
      });

      // Cast the result to HTMLButtonElement so that TypeScript knows it has a click method
      const googleButtonWrapper = googleLoginWrapper.querySelector(
        'div[role=button]',
      ) as HTMLButtonElement;

      return googleButtonWrapper;
    };
    const googleButtonWrapper = createFakeGoogleWrapper();
    let windowObject: any = window;
    windowObject.handleGoogleLogin = (a: any) => {
      // Use wrapper click to prevent Illegal invocation exception
      googleButtonWrapper.click();
    };
    gAccounts.id.prompt(); // Optional: To show One Tap dialog automatically
  }

  handleCredentialResponse(authToken: any) {
    if (authToken) {
      this.loginBySSO(authToken, ProviderType.Google);
    }
  }
  /**
   * Initializes the login form
   */
  private initForm(): void {
    this.loginForm = this.fb.group({
      email: [
        null,
        [
          Validators.required,
          Validators.pattern(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/),
        ],
      ],
      password: [null, [Validators.required]],
    });
  }

  /**
   *  Getters for the  form controls
   *    */

  get password() {
    return this.loginForm?.get('password');
  }

  get email() {
    return this.loginForm?.get('email');
  }

  /**
   * Handles form submission
   */
  onSubmit(): void {
    const { email, password } = this.loginForm.value;

    const payload = {
      email,
      password,
      api_client_name: 'app-web-client',
      mobile_number: 'null',
      platform_id: '1',
      firebase_token: 'null',
      ip_address: 'null',
      os_version: 'null',
      version_no: 'null',
      mobile_operator: 'null',
      is_use_new_firebase_token: true,
      device_identifier: 'null',
      language: 'null',
    };

    this.authenticationService
      .login(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.authenticationService.saveUserData(response.Data);
        },
        error: error => {
          this.loginForm.setErrors({ invalidLogin: true });
        },
        complete: () => {
          this._per.refreshPermissions();
          this.router.navigate(['/']);

          const apiKey = this.authenticationService.getDispatchApiKey();
          externalStoreApiKeyService(apiKey);
        },
      });
  }

  /**
   * toggle Password Field Visibility
   */
  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }

  handleKeydown(event: KeyboardEvent): void {
    // Allow toggling visibility on 'Enter' or 'Space' key
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault(); // Prevent space scrolling
      this.togglePasswordVisibility();
    }
  }

  onBlurEmail(): void {
    this.emailTouched = true; // Mark email as touched manually on blur
  }

  public signInWithMicrosoft(): void {
    this._msalService
      .loginPopup({
        scopes: ['User.Read', 'openid', 'profile', 'email'], // Add required scopes
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: AuthenticationResult) => {
          const token = response?.accessToken;
          if (token) {
            this.loginBySSO(token, ProviderType.Microsoft);
          }
        },
        error: error => {
          console.error('Microsoft Login Failed:', error);
        },
      });
  }
  public loginBySSO(accessToken: string, providerType: number): void {
    this.authenticationService
      .loginBySSO({
        token: accessToken,
        providerType: providerType,
        clientName: 'app-web-client', // Customize as per your needs
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.authenticationService.saveUserData(response.Data);
        },
        complete: () => {
          this.router.navigate(['/']);

          const apiKey = this.authenticationService.getDispatchApiKey();
          externalStoreApiKeyService(apiKey);
        },
        error: (error: any) => {
          console.error('Google Login Failed:', error);
        },
      });
  }

  saveLoginUser(Response: any) {
    let currentUser: UserV2 = {};
    currentUser.id = Response.Data.user_profile.id;
    currentUser.email = Response.Data.user_profile.email;
    currentUser.first_name = Response.Data.user_profile.first_name;
    currentUser.last_name = Response.Data.user_profile.last_name;
    currentUser.full_name = Response.Data.user_profile.full_name;
    currentUser.mobile_number = Response.Data.user_profile.mobile_number;
    currentUser.nick_name = Response.Data.user_profile.nick_name;
    currentUser.role = Response.Data.user_profile.role;
    currentUser.avatar = Response.Data.user_profile.profile_picture_url;
    currentUser.access_token = Response.Data.user_profile.access_token;
    currentUser.Permissions = Response.Data.user_profile.Permissions;
    currentUser.PortalName = Response.Data.user_profile.PortalName;
    currentUser.client_id = Response.Data.identity.client_id;
    currentUser.OrganizationId = Response.Data.identity.OrganizationId;
    currentUser.organizationStatusId =
      Response.Data.identity.organizationstatusId;
    currentUser.PortalType = Response.Data.user_profile.PortalType;
    if (currentUser.PortalType == PortalTypeEnum.ServiceProvider) {
      let splittedName = currentUser.full_name?.split(' ');
      if (splittedName) {
        if (splittedName.length > 0) {
          if (currentUser.first_name && currentUser.last_name) {
            currentUser.short_name =
              currentUser.first_name[0] + currentUser.last_name[0];
          }
        }
      }

      this.authenticationService.saveUserDataV2(currentUser);
    } else {
      this._msalService.logoutRedirect();
    }
  }
  loginDevelopment(currentUser: any) {
    //find the first permession into investigate to it after
    let pages = currentUser.Permissions.filter(
      (element: any, index: any, array: any) => {
        let exist = this.menuList.find(
          (x: any) => x.PermissionValue == element,
        );
        if (exist) {
          return element;
        }
      },
    );
    let navigatURL = this.menuList.find(
      (x: any) => x.PermissionValue == pages[0],
    );
    if (navigatURL == undefined) {
      navigatURL = '/pages/miscellaneous/error';
      window.location.href = navigatURL;
    } else {
      setTimeout(() => {
        localStorage.setItem('defalutnavigation', navigatURL.url.toString());
        window.location.href = navigatURL.url;
        this.authenticationService.saveUserData(currentUser);
      }, 500);
    }
  }
  public handleGoogleLoginFunction(): void {
    (window as any).handleGoogleLogin();
  }
}
